import { AutocompleteWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import { ButtonBase } from '@material-ui/core'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import DeleteIcon from '@material-ui/icons/Delete'
import React, { useEffect, useState } from 'react'

import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { GATEWAYS_LIST_QUERY } from '../../graphql/gateways'
import { getTranslate, lang } from '../../localization'
import { Accommodation } from '../../types/accommodation'
import { Gateway } from '../../types/gateway'
import { AssociatedGateway } from '../../types/sitekey'
import {
  rawSiteKeyCode,
  siteKeyAccommodationOptions
} from './siteKeyAccommodationOptions'
import { siteKeyGatewayOptions } from './siteKeyGatewayOptions'
import useStyles from './styles/SitekeyBookingRedirect.styles'

type SiteKeyAssociatedGatewayProps = {
  associatedGateways: AssociatedGateway[]
  index: number
  setAssociatedGateways: React.Dispatch<
    React.SetStateAction<AssociatedGateway[]>
  >
  isDefault: boolean
}

export const SiteKeyAssociatedGateway: React.FC<
  SiteKeyAssociatedGatewayProps
> = ({ associatedGateways, index, setAssociatedGateways, isDefault }) => {
  const classes = useStyles()

  const [
    autoCompleteLabelForAccommodation,
    setAutoCompleteLabelForAccommodation
  ] = useState<any>(null)

  const [autoCompleteLabelGateways, setautoCompleteLabelGateways] = useState<
    any[]
  >([])

  const { data: accommodations, loading: loadingAccs } = useQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const { data: gatewaysData, loading: loadingForGateways } = useQuery<{
    gateways: Gateway[]
  }>(GATEWAYS_LIST_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const options = React.useMemo(
    () =>
      siteKeyAccommodationOptions(
        accommodations?.accommodations,
        getTranslate('Default'),
        lang
      ),
    [accommodations?.accommodations]
  )

  const found = options.find(
    (acc) => acc.value === associatedGateways[index].Code
  )

  // A row saved before the raw-code fix holds an acc_ token; store its raw code instead.
  useEffect(() => {
    const code = associatedGateways[index].Code
    const raw = rawSiteKeyCode(code, accommodations?.accommodations)
    if (raw !== code) {
      setAssociatedGateways((preState) => [
        ...preState.slice(0, index),
        { ...preState[index], Code: raw },
        ...preState.slice(index + 1)
      ])
    }
  }, [accommodations?.accommodations])

  useEffect(() => {
    if (options) {
      setAutoCompleteLabelForAccommodation({
        value: found?.value,
        label: found?.label
      })
    }
  }, [options, isDefault, associatedGateways[index].Code])

  useEffect(() => {
    const ids = associatedGateways[index].GatewayIds || []
    setautoCompleteLabelGateways(
      siteKeyGatewayOptions(ids, gatewaysData?.gateways)
    )
  }, [associatedGateways, gatewaysData?.gateways, index])

  return (
    <div className={classes.root}>
      <AutocompleteWithLabel
        label={getTranslate(isDefault ? 'Default' : 'Accommodation')}
        placeholder={getTranslate('Please Select Accommodation')}
        value={autoCompleteLabelForAccommodation}
        getOptionSelected={(option, value) => option.value === value.value}
        onChange={(_, value: any) => {
          if (value) {
            setAutoCompleteLabelForAccommodation({
              value: value.value,
              label: value.label
            })

            setAssociatedGateways((preState) => [
              ...preState.slice(0, index),
              {
                Code: value.value,
                GatewayType: isDefault ? 'Provider' : 'Accommodation',
                GatewayIds: preState[index].GatewayIds
              },
              ...preState.slice(index + 1)
            ])
          } else {
            setAutoCompleteLabelForAccommodation(null)

            setAssociatedGateways((preState) => [
              ...preState.slice(0, index),
              {
                Code: '',
                GatewayType: '',
                GatewayIds: preState[index].GatewayIds
              },
              ...preState.slice(index + 1)
            ])
          }
        }}
        options={options}
        disabled={isDefault}
        size="small"
        inputSize="small"
        loading={loadingAccs}
        loadingText={getTranslate('Loading') + '...'}
      />
      <AutocompleteWithLabel
        name="Gateways"
        inputSize="small"
        multiple
        loading={loadingForGateways}
        loadingText={getTranslate('Loading') + '...'}
        options={
          gatewaysData?.gateways.map((item) => {
            return {
              value: item.basicDetails.gatewayId,
              label: item.basicDetails.name
            }
          }) || []
        }
        size="medium"
        label={getTranslate('Gateways')}
        placeholder={getTranslate('Select the Gateway')}
        value={autoCompleteLabelGateways}
        onChange={(_, v: any) => {
          if (v) {
            const selectedGateways: string[] = []
            v.map((value: any) => selectedGateways.push(value.value))

            setAssociatedGateways((preState) => [
              ...preState.slice(0, index),
              {
                Code: preState[index].Code,
                GatewayType: preState[index].GatewayType,
                GatewayIds: selectedGateways
              },
              ...preState.slice(index + 1)
            ])
          } else {
            setAssociatedGateways((preState) => [
              ...preState.slice(0, index),
              {
                Code: preState[index].Code,
                GatewayType: preState[index].GatewayType,
                GatewayIds: []
              },
              ...preState.slice(index + 1)
            ])
          }
        }}
      />
      <div className={classes.buttonsContainer}>
        <ButtonBase
          className={[
            classes.greenColor,
            associatedGateways.length - 1 > index && classes.greyColor
          ].join(' ')}
          onClick={() => {
            setAssociatedGateways((preState) => {
              return [
                ...preState,
                {
                  Code: '',
                  GatewayType: '',
                  GatewayIds: []
                }
              ]
            })
          }}>
          <AddCircleOutlineIcon />
        </ButtonBase>
        <ButtonBase
          className={classes.redColor}
          onClick={() => {
            setAssociatedGateways((preState) => [
              ...preState.slice(0, index),
              ...preState.slice(index + 1)
            ])
          }}>
          <DeleteIcon />
        </ButtonBase>
      </div>
    </div>
  )
}
